import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ACCENT = '#5E0ED7';
const ease = [0.22, 1, 0.36, 1];

export default function AdminLogin() {
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [showPass, setShowPass]   = useState(false);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const { login }                 = useAuth();
  const navigate                  = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin/dashboard');
    } catch (err) {
      setError('Invalid credentials. Check email and password.');
    } finally {
      setLoading(false);
    }
  }

  const inputBase = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    padding: '10px 14px',
    fontSize: 14,
    color: '#fff',
    width: '100%',
    outline: 'none',
    transition: 'border-color 160ms ease',
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#08070f' }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(94,14,215,0.12) 0%, transparent 70%)' }} />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease }}
        className="w-full max-w-md relative"
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img src="/logo.png" alt="Syenovix" className="w-16 h-16 rounded-2xl object-cover mb-4" style={{ filter: 'drop-shadow(0 0 12px rgba(94,14,215,0.5))' }} />
          <h1 className="text-xl font-bold tracking-widest uppercase text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Admin Portal</h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Syenovix Content Management</p>
        </div>

        {/* Card */}
        <div className="rounded-[1.75rem] p-[1px]" style={{ background: 'linear-gradient(135deg, rgba(94,14,215,0.4), rgba(255,255,255,0.06))' }}>
          <div className="rounded-[1.7rem] p-8" style={{ background: '#0f0d1a' }}>
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">

              {/* Email */}
              <div>
                <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>Email</label>
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  placeholder="admin@syenovix.com"
                  style={inputBase}
                  onFocus={e => e.target.style.borderColor = ACCENT}
                  onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.10)'}
                />
              </div>

              {/* Password with show/hide toggle */}
              <div>
                <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>Password</label>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password} onChange={e => setPassword(e.target.value)} required
                    placeholder="••••••••"
                    style={{ ...inputBase, paddingRight: 44 }}
                    onFocus={e => e.target.style.borderColor = ACCENT}
                    onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.10)'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-colors"
                    style={{ color: 'rgba(255,255,255,0.4)' }}
                    tabIndex={-1}
                  >
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && <p className="text-xs text-red-400 bg-red-400/10 px-3 py-2 rounded-lg">{error}</p>}

              <motion.button
                type="submit"
                whileTap={{ scale: 0.97 }}
                disabled={loading}
                className="w-full py-3.5 rounded-xl text-sm font-bold tracking-wider uppercase text-white mt-1"
                style={{ background: ACCENT, opacity: loading ? 0.7 : 1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </motion.button>

              {/* Forgot password link */}
              <div className="text-center">
                <Link
                  to="/admin/forgot-password"
                  className="text-xs font-medium"
                  style={{ color: 'rgba(255,255,255,0.35)' }}
                >
                  Forgot your password?
                </Link>
              </div>
            </form>
          </div>
        </div>

        <p className="text-center text-xs mt-6" style={{ color: 'rgba(255,255,255,0.2)' }}>
          Access restricted to authorized Syenovix admins
        </p>
      </motion.div>
    </div>
  );
}
